import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CompanyContext = createContext(null);

const DEFAULT_BRANDING = {
  company_name: 'TOYOTA MATERIAL HANDLING WAREHOUSE SOLUTIONS THAILAND',
  company_subtitle: 'Warehouse Solutions Thailand',
  logo_url: '',
  logo_data: '',
  updated_at: ''
};

export function CompanyProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBranding = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/settings/company', {
        headers: { 'Cache-Control': 'no-cache' }
      });
      if (res.ok) {
        const json = await res.json();
        if (json.status === 'success' && json.data) {
          setBranding(json.data);
        }
      }
    } catch (err) {
      console.warn('Failed to fetch company branding:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  const updateBranding = async (formDataOrJson) => {
    try {
      let options = {};
      if (formDataOrJson instanceof FormData) {
        options = {
          method: 'POST',
          body: formDataOrJson
        };
      } else {
        options = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formDataOrJson)
        };
      }

      const res = await fetch('/api/settings/company', options);
      const json = await res.json();
      if (json.status === 'success' && json.data) {
        setBranding(json.data);
        return { success: true, data: json.data };
      } else {
        throw new Error(json.message || 'Failed to update branding');
      }
    } catch (err) {
      console.error('Error saving company branding:', err);
      return { success: false, error: err.message };
    }
  };

  const resetBranding = async () => {
    try {
      const res = await fetch('/api/settings/company/reset', { method: 'POST' });
      const json = await res.json();
      if (json.status === 'success' && json.data) {
        setBranding(json.data);
        return { success: true };
      }
      throw new Error(json.message || 'Failed to reset branding');
    } catch (err) {
      console.error('Error resetting company branding:', err);
      return { success: false, error: err.message };
    }
  };

  const activeLogo = branding.logo_data || branding.logo_url || '';
  const hasCustomLogo = Boolean(activeLogo);

  return (
    <CompanyContext.Provider
      value={{
        branding,
        activeLogo,
        hasCustomLogo,
        companyName: branding.company_name || DEFAULT_BRANDING.company_name,
        companySubtitle: branding.company_subtitle || DEFAULT_BRANDING.company_subtitle,
        loading,
        error,
        updateBranding,
        resetBranding,
        refreshBranding: fetchBranding
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const context = useContext(CompanyContext);
  if (!context) {
    // Return safe default if rendered outside provider
    return {
      branding: DEFAULT_BRANDING,
      activeLogo: '',
      hasCustomLogo: false,
      companyName: DEFAULT_BRANDING.company_name,
      companySubtitle: DEFAULT_BRANDING.company_subtitle,
      loading: false,
      error: null,
      updateBranding: async () => ({ success: false }),
      resetBranding: async () => ({ success: false }),
      refreshBranding: () => {}
    };
  }
  return context;
}
